import TagManager from 'react-gtm-module';
import { AccountDataType } from 'types/account';
import { getParentAccountID } from './commonUtil';
import { isDemoENV, isProductionENV } from './envUtil';

// Check if GTM should be enabled
const env_check = isDemoENV() || isProductionENV();

// GTM Data Layer interface
interface GTMDataLayer {
    event?: string;
    [key: string]: any;
}

/**
 * Push data to GTM Data Layer
 */
const pushToDataLayer = (data: GTMDataLayer) => {
    if (env_check && window && (window as any).dataLayer) {
        (window as any).dataLayer.push(data);
    }
};

/**
 * Set user properties in GTM Data Layer
 */
const setUserProperties = (account: AccountDataType) => {
    const parent_account_id = getParentAccountID(account);

    pushToDataLayer({
        event: 'userPropertiesSet',
        userId: account.account_id.toString(),
        userEmail: account.email,
        userName: account.fullname,
        parentAccountId: parent_account_id,
        isParentAccount: account.account_id === parent_account_id,
        userTypeId: account.user_type_id,
        accountType: account.account_type,
        accountRoleId: account.accountrole_id,
    });
};

const GTMUtil = {
    /**
     * Initialize GTM
     */
    initialize: (gtmId: string) => {
        if (env_check && gtmId) {
            TagManager.initialize({ gtmId });
        }
    },

    /**
     * Track page view
     */
    trackPageView: (path: string, title?: string) => {
        pushToDataLayer({
            event: 'pageview',
            pagePath: path,
            pageTitle: title || document.title,
        });
    },

    /**
     * Track custom events
     */
    trackEvent: (eventName: string, eventData?: Record<string, any>) => {
        pushToDataLayer({
            event: eventName,
            ...eventData,
        });
    },

    /**
     * Track user signup completion
     */
    trackSignupComplete: (
        method: string,
        accountType: string,
        planName: string,
        planCode: string
    ) => {
        pushToDataLayer({
            event: 'signup_complete',
            signupMethod: method,
            accountType: accountType,
            planName: planName,
            planCode: planCode,
        });
    },

    /**
     * Track user login
     */
    trackLogin: (method: string) => {
        pushToDataLayer({
            event: 'login',
            loginMethod: method,
        });
    },

    /**
     * Track transaction creation
     */
    trackTransactionCreated: (transactionId: string | number) => {
        pushToDataLayer({
            event: 'transaction_created',
            transactionId: transactionId.toString(),
        });
    },

    /**
     * Track prospect creation
     */
    trackProspectCreated: (prospectId: string | number) => {
        pushToDataLayer({
            event: 'prospect_created',
            prospectId: prospectId.toString(),
        });
    },

    /**
     * Track document upload
     */
    trackDocumentUpload: (documentType: string) => {
        pushToDataLayer({
            event: 'document_upload',
            documentType,
        });
    },

    /**
     * Track email sent
     */
    trackEmailSent: (emailType: string) => {
        pushToDataLayer({
            event: 'email_sent',
            emailType,
        });
    },

    /**
     * Identify user and set user properties
     */
    identifyUser: (account: AccountDataType) => {
        if (env_check) {
            setUserProperties(account);
        }
    },

    /**
     * Set custom user properties
     */
    setUserProperty: (propertyName: string, propertyValue: any) => {
        pushToDataLayer({
            event: 'userPropertySet',
            [propertyName]: propertyValue,
        });
    },

    /**
     * Clear user data (on logout)
     */
    clearUser: () => {
        pushToDataLayer({
            event: 'userLogout',
            userId: undefined,
            userEmail: undefined,
            userName: undefined,
        });
    },
};

export { GTMUtil };
